import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { AdTopBanner, AdInContent, AdBottomBanner } from '../components/AdSense';

const InterestRateCalculatorPage = () => {
  const [presentValue, setPresentValue] = useState('10000');
  const [futureValue, setFutureValue] = useState('15000');
  const [years, setYears] = useState('5');
  const [compounding, setCompounding] = useState('1');
  const [result, setResult] = useState(null);

  const calculate = () => {
    const pv = parseFloat(presentValue) || 0;
    const fv = parseFloat(futureValue) || 0;
    const t = parseFloat(years) || 1;
    const n = parseInt(compounding) || 1;

    if (pv <= 0 || fv <= 0 || t <= 0) return;

    // r = n * ((FV/PV)^(1/(n*t)) - 1)
    const rate = n * (Math.pow(fv / pv, 1 / (n * t)) - 1) * 100;
    const totalGain = fv - pv;
    const percentGain = ((fv - pv) / pv) * 100;

    setResult({ rate: rate.toFixed(4), totalGain, percentGain: percentGain.toFixed(2), pv, fv, years: t });
  };

  const formatCurrency = (num) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex flex-col">
      <Header />
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <AdTopBanner />
          <nav className="text-sm text-gray-500 mb-4">
            <Link to="/home" className="text-cyan-400 hover:text-cyan-300">Home</Link><span className="mx-2">›</span>
            <span className="text-gray-400">Interest Rate Calculator</span>
          </nav>
          <h1 className="text-3xl font-bold text-white mb-4">Interest Rate Calculator</h1>
          <p className="text-gray-400 mb-6">Calculate the interest rate needed to grow an investment from present value to future value.</p>

          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 w-32">Present Value</label>
                <div className="flex items-center gap-1"><span className="text-gray-400">$</span>
                  <Input type="number" value={presentValue} onChange={(e) => setPresentValue(e.target.value)}
                    className="w-32 bg-[#0d0d0d] border-[#2a2a2a] text-white" /></div>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 w-32">Future Value</label>
                <div className="flex items-center gap-1"><span className="text-gray-400">$</span>
                  <Input type="number" value={futureValue} onChange={(e) => setFutureValue(e.target.value)}
                    className="w-32 bg-[#0d0d0d] border-[#2a2a2a] text-white" /></div>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 w-32">Time Period</label>
                <div className="flex items-center gap-2">
                  <Input type="number" value={years} onChange={(e) => setYears(e.target.value)}
                    className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white" /><span className="text-gray-400">years</span></div>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 w-32">Compounding</label>
                <select value={compounding} onChange={(e) => setCompounding(e.target.value)}
                  className="px-3 py-2 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-white text-sm">
                  <option value="1">Annually</option><option value="2">Semi-annually</option>
                  <option value="4">Quarterly</option><option value="12">Monthly</option><option value="365">Daily</option>
                </select>
              </div>
            </div>
            <Button onClick={calculate} className="mt-6 bg-cyan-600 hover:bg-cyan-500 text-white px-8">Calculate</Button>

            {result && (
              <div className="mt-6 p-5 bg-[#0d0d0d] border border-[#2a2a2a] rounded-xl">
                <div className="text-center mb-6">
                  <p className="text-gray-400 mb-2">Required Interest Rate</p>
                  <p className="text-4xl font-bold text-green-400">{result.rate}%</p>
                  <p className="text-gray-500 mt-2">to grow {formatCurrency(result.pv)} to {formatCurrency(result.fv)} in {result.years} years</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-[#1a1a1a] rounded-lg text-center">
                    <p className="text-gray-400 text-sm">Total Gain</p>
                    <p className="text-cyan-400 font-bold">{formatCurrency(result.totalGain)}</p>
                  </div>
                  <div className="p-3 bg-[#1a1a1a] rounded-lg text-center">
                    <p className="text-gray-400 text-sm">Percent Gain</p>
                    <p className="text-cyan-400 font-bold">{result.percentGain}%</p>
                  </div>
                </div>
              </div>
            )}
          </div>
          <AdInContent />
          <div className="mt-10 p-6 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl">
            <h3 className="text-lg font-semibold text-white mb-4">Financial Calculators</h3>
            <div className="flex flex-wrap gap-2">
              {[{ name: 'Interest', path: '/interest-calculator' }, { name: 'Compound Interest', path: '/compound-interest-calculator' }, { name: 'Investment', path: '/investment-calculator' }].map((calc) => (
                <Link key={calc.path} to={calc.path} className="px-3 py-1.5 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-cyan-400 hover:text-cyan-300 text-sm">{calc.name}</Link>
              ))}
            </div>
          </div>
          <AdBottomBanner />
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default InterestRateCalculatorPage;
